import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Stack,
} from '@chakra-ui/react';
import { FieldType, RouterTargetType } from '@common/entities';
import { newUuid } from '@common/services';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationStatus } from 'enums';

import { TrashIcon } from 'components/custom-icons';
import { SmartRouteLayout } from 'components/layout';
import Loader from 'components/Loader';
import { ConfirmationModal } from 'components/modal';
import notification from 'components/notification';
import { DeleteConfirmationMessage } from 'components/smart-routes/DeleteConfirmationMessage';
import SmartRouteFieldRenderer from 'components/smart-routes/SmartRouteFieldRenderer';
import { useApplicationsAndFolders } from 'hooks/queries/useApplications';
import { addSingleField, deleteField, patchField } from 'services';
import { getSmartRoute } from 'services/smart-routes';
import { getAvailableSteps } from 'utils/smart-routes/getAvailableSteps';
import {
  ensureFieldRegisteredAndSet,
  findDependentQuestions,
} from 'utils/smart-routes/smartRouteUtils';

const PLACEHOLDER_QUESTION = {
  id: 'placeholder',
  name: 'placeholder',
  sequence: -1,
  type: 'placeholder',
};

function ViewSmartRoute() {
  const { routeId, versionId } = useParams();
  const methods = useForm();
  const { trigger, clearErrors, setValue, register } = methods;
  const ensureField = ensureFieldRegisteredAndSet(register, setValue, clearErrors);

  const queryClient = useQueryClient();
  const [questions, setQuestions] = useState<any[]>([]);
  const [sectionId, setSectionId] = useState('');
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [dependentQuestions, setDependentQuestions] = useState<any[]>([]);
  const [optionToDelete, setOptionToDelete] = useState<{
    questionId: string;
    optionId: string;
  } | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isOptionConfirmOpen, setIsOptionConfirmOpen] = useState(false);
  const [isAddingField, setIsAddingField] = useState(false);

  const { data: applicationsData } = useApplicationsAndFolders();

  const { data, isPending, fetchStatus, refetch } = useQuery({
    queryKey: ['smart-route', routeId],
    queryFn: () => getSmartRoute(routeId, versionId),
    enabled: !!routeId && !!versionId,
  });

  const { mutateAsync: addOptionField } = useMutation({
    mutationFn: addSingleField,
    onError: (err) => notification('Error', err.message, NotificationStatus.ERROR),
  });

  const { mutateAsync: updateField } = useMutation({ mutationFn: patchField });

  const { mutateAsync: deleteQuestion, isPending: isDeleting } = useMutation({
    mutationFn: deleteField,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['smart-route', routeId] }),
    onError: (err) => notification('Error', err.message, NotificationStatus.ERROR),
  });

  useEffect(() => {
    refetch();
  }, [refetch, versionId]);

  useEffect(() => {
    const firstSection = data?.data?.versions?.[0]?.form?.pages?.[0]?.sections?.[0];
    if (firstSection) {
      setSectionId(firstSection.id);
      setQuestions(firstSection.elements || []);
    }
  }, [data]);

  const handleAddQuestion = async (
    questionType: 'message' | 'dropdown',
    autoTargetInfo?: {
      autoTarget: boolean;
      fieldId: string;
      optionId: string;
      optionName: string;
    },
  ) => {
    setIsAddingField(true);

    try {
      const newFieldSequence = questions.length + 1;
      const namePrefix = questionType === 'message' ? 'message' : 'question';
      const label = questionType === 'message' ? 'Message' : 'Qualifying question';
      const fieldType = questionType === 'message' ? 'static-text' : FieldType.Dropdown;

      const optionPayload = {
        sectionId,
        customType: '',
        customField: true,
        label,
        name: `${namePrefix}_${newFieldSequence}_option_${newUuid()}`,
        required: true,
        sequence: newFieldSequence,
        type: fieldType,
        ...(questionType !== 'message' && {
          options: [
            { name: `option_1_${newUuid()}`, label: 'Option 1', sequence: 1 },
            { name: `option_2_${newUuid()}`, label: 'Option 2', sequence: 2 },
          ],
        }),
      };

      setQuestions((prev) => [...prev, PLACEHOLDER_QUESTION]);
      const response = await addOptionField(optionPayload);

      setQuestions((prev) => prev.map((q) => (q.id === 'placeholder' ? response.data : q)));

      const newQuestionId = response?.data?.id;

      if (autoTargetInfo?.autoTarget && newQuestionId) {
        const fieldToUpdate = questions.find((q) => q.id === autoTargetInfo.fieldId);
        if (fieldToUpdate) {
          const updatedOptions = fieldToUpdate.options.map((opt) =>
            opt.id === autoTargetInfo.optionId
              ? {
                  ...opt,
                  target: { id: newQuestionId, type: RouterTargetType.Field },
                  routeToFieldId: newQuestionId,
                }
              : opt,
          );
          await updateField({ id: fieldToUpdate.id, options: updatedOptions });

          await ensureField(autoTargetInfo.optionName, newQuestionId);
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ['smart-route', routeId],
      });
      const result = await refetch();

      const firstSection = result?.data?.data?.versions?.[0]?.form?.pages?.[0]?.sections?.[0];

      if (firstSection) {
        setSectionId(firstSection.id);
        setQuestions(firstSection.elements);
      }
    } catch (err: any) {
      notification('Error', err?.message || 'Failed to add question.', NotificationStatus.ERROR);
    } finally {
      setIsAddingField(false);
    }
  };

  const handleDeleteClick = (questionId: string) => {
    const dependents = findDependentQuestions(questions, questionId);
    setDependentQuestions(dependents);
    setQuestionToDelete(questionId);
    setIsConfirmOpen(true);
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;
    try {
      await deleteQuestion(questionToDelete);
      setQuestions((prev) => prev.filter((q) => q.id !== questionToDelete));
    } catch (err) {
      notification('Error', err?.message || 'Failed to delete question.', NotificationStatus.ERROR);
    } finally {
      setIsConfirmOpen(false);
      setQuestionToDelete(null);
    }
  };

  const handleAddOption = async (questionId: string) => {
    const currentQuestion = questions.find((q) => q.id === questionId);
    if (!currentQuestion) return;

    try {
      const lastOption = currentQuestion.options[currentQuestion.options.length - 1];
      const newOptionSequence = lastOption ? lastOption.sequence + 1 : 1;

      const newOption = {
        name: `option_${newUuid()}`,
        label: `Option ${newOptionSequence}`,
        sequence: newOptionSequence,
      };

      const response = await patchField({
        id: currentQuestion.id,
        options: [...currentQuestion.options, newOption],
      });

      const updatedQuestion = response?.data?.options
        ? { ...currentQuestion, options: response.data.options }
        : currentQuestion;

      setQuestions((prevQuestions) =>
        prevQuestions.map((q) => (q.id === questionId ? updatedQuestion : q)),
      );
    } catch (err: any) {
      notification('Error', err?.message || 'Failed to add option.', NotificationStatus.ERROR);
    }
  };

  const handleDeleteOption = (questionId: string, optionId: string) => {
    setOptionToDelete({ questionId, optionId });
    setIsOptionConfirmOpen(true);
  };

  const handleConfirmDeleteOption = async () => {
    if (!optionToDelete) return;

    const { questionId, optionId } = optionToDelete;
    const currentQuestion = questions.find((q) => q.id === questionId);
    if (!currentQuestion) return;

    try {
      const updatedOptions = currentQuestion.options.filter((opt) => opt.id !== optionId);
      await updateField({ id: currentQuestion.id, options: updatedOptions });

      setQuestions((prev) =>
        prev.map((q) => (q.id === questionId ? { ...q, options: updatedOptions } : q)),
      );

      queryClient.invalidateQueries({ queryKey: ['smart-route', routeId] });
    } catch (err: any) {
      notification('Error', err?.message || 'Failed to delete option.', NotificationStatus.ERROR);
    } finally {
      setIsOptionConfirmOpen(false);
      setOptionToDelete(null);
    }
  };

  const validateBeforePublish = async () => {
    return trigger();
  };

  const topActionProps = {
    data: data?.data,
    validateBeforePublish,
    refetch,
  };

  if (isPending && fetchStatus !== 'idle') return <Loader />;

  const isDraftMode = data?.data?.versions?.[0]?.status === 'draft';
  let staticTextCount = 0;
  let qualifyingQuestionCount = 0;
  return (
    <FormProvider {...methods}>
      <SmartRouteLayout topActionProps={topActionProps}>
        <Box as="form" mb="100px">
          <Stack spacing={6} my={6}>
            {questions.map((question) => {
              if (question.type === 'placeholder') {
                return (
                  <Card key={question.id}>
                    <CardBody>
                      <Center p={6}>
                        <Spinner size="lg" />
                      </Center>
                    </CardBody>
                  </Card>
                );
              }

              const { name, type } = question;
              const isOpeningMessage = name === 'application_route_opening_message';
              const isStaticText = type === 'static-text';

              let heading = '';
              if (isOpeningMessage) {
                heading = 'Opening Message';
              } else if (isStaticText) {
                staticTextCount += 1;
                heading = `Message ${staticTextCount}`;
              } else {
                qualifyingQuestionCount += 1;
                heading = `Qualifying Question ${qualifyingQuestionCount}`;
              }

              return (
                <Card key={question.id}>
                  <CardHeader pb={2} display="flex" gap={2} alignItems="center">
                    <Heading>{heading}</Heading>
                    {!isOpeningMessage && isDraftMode && (
                      <IconButton
                        aria-label="trash"
                        variant="ghost"
                        fontSize="26px"
                        color="red.700"
                        icon={<TrashIcon />}
                        onClick={() => handleDeleteClick(question.id)}
                      />
                    )}
                  </CardHeader>
                  <CardBody>
                    <SmartRouteFieldRenderer
                      section={{ id: question.id, elements: [question] }}
                      onChangeHandler={(id, newLabel) =>
                        setQuestions((prev) =>
                          prev.map((q) => (q.id === id ? { ...q, label: newLabel } : q)),
                        )
                      }
                      onDeleteOption={(optionId) => handleDeleteOption(question.id, optionId)}
                      getAvailableSteps={() =>
                        getAvailableSteps(questions, applicationsData, question.id)
                      }
                      updateField={updateField}
                      setQuestions={setQuestions}
                      onAddQuestion={handleAddQuestion}
                      isDisabled={!isDraftMode}
                    />
                    {!isStaticText && isDraftMode && (
                      <Button onClick={() => handleAddOption(question.id)}>Add Option</Button>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </Stack>

          {isDraftMode && (
            <HStack justifyContent="center" gap={4}>
              <Button
                mt={4}
                onClick={() => handleAddQuestion('dropdown')}
                isLoading={isAddingField}
                loadingText="Updating"
                variant="action"
              >
                Add Question
              </Button>
              <Button
                mt={4}
                onClick={() => handleAddQuestion('message')}
                isLoading={isAddingField}
                loadingText="Updating"
                variant="action"
              >
                Add Message
              </Button>
            </HStack>
          )}

          <ConfirmationModal
            isOpen={isConfirmOpen}
            onClose={() => {
              setIsConfirmOpen(false);
              setQuestionToDelete(null);
              setDependentQuestions([]);
            }}
            onSubmit={handleDeleteQuestion}
            message={
              <DeleteConfirmationMessage
                questions={questions}
                questionToDelete={questionToDelete}
                dependentQuestions={dependentQuestions}
              />
            }
            actionLabel="Delete"
            actionColor="red.500"
            isLoading={isDeleting}
          />

          <ConfirmationModal
            isOpen={isOptionConfirmOpen}
            onClose={() => setIsOptionConfirmOpen(false)}
            onSubmit={handleConfirmDeleteOption}
            message="Are you sure you wish to delete this option?"
            actionLabel="Delete"
            actionColor="red.500"
            isLoading={isDeleting}
          />
        </Box>
      </SmartRouteLayout>
    </FormProvider>
  );
}

export default ViewSmartRoute;
